import { Query } from "./Query.js";
import { QueryOperators } from "./QueryOperators.js";
import { QuerySegment } from "./QuerySegment.js";
/**
 * Represents a sensenet Content Query expression
 */

export class QueryExpression extends QuerySegment {
  constructor() {
    super(...arguments);
    this.subQuery = void 0;
  }

  /**
   * A plain string as Query term
   * @param {string} term The Query term
   * @returns { QueryOperator<TReturns> } The Next query operator (fluent)
   */
  term(term) {
    this.stringValue = term;
    this.segmentType = 'term';
    return this.finialize();
  }
  /**
   * A plain string as Query string
   * @param {string} explicitQuery The Query string
   * @returns { QueryOperator<TReturns> } The Next query operator (fluent)
   */


  explicitQuery(explicitQuery) {
    this.stringValue = `${explicitQuery}`;
    this.segmentType = 'explicitQuery';
    return this.finialize();
  }
  /**
   * Adds an InTree content query expression
   * @param {string | Content } path The path string or content that will be used as a root
   * @returns { QueryOperator<TReturns> } The Next query operator (fluent)
   */


  inTree(path) {
    this.stringValue = `InTree:"${path}"`;
    this.segmentType = 'inTree';
    return this.finialize();
  }
  /**
   * Adds an InFolder content query expression
   * @param {string | Content } path The path string or content that will be used as a root
   * @returns { QueryOperator<TReturns> } The Next query operator (fluent)
   */


  inFolder(path) {
    this.stringValue = `InFolder:"${path}"`;
    this.segmentType = 'inFolder';
    return this.finialize();
  }
  /**
   * Adds a Type content query expression and casts the rest of the expression to a new type
   * @param { new (...args: any[]) => TNewType } newTypeAssertion The path string or content that will be used as a root
   * @returns { QueryOperator<TNewType> } The Next query operator (fluent)
   */


  type(newTypeAssertion) {
    this.stringValue = `Type:${newTypeAssertion}`;
    this.segmentType = 'type';
    return this.finialize();
  }
  /**
   * Adds a TypeIs content query expression and casts the rest of the expression to a new type
   * @param { new (...args: any[]) => TNewType } newTypeAssertion The path string or content that will be used as a root
   * @returns { QueryOperator<TNewType> } The Next query operator (fluent)
   */


  typeIs(newTypeAssertion) {
    this.stringValue = `TypeIs:${newTypeAssertion}`;
    this.segmentType = 'typeIs';
    return this.finialize();
  }
  /**
   * Field equality check content query expression (e.g. +FieldName:'value')
   * @param { K } FieldName The name of the Field to be checked
   * @param { TReturns[K] } value The value that will be checked. You can use '?' and '*' wildcards
   * @returns { QueryOperator<TReturns> } The Next query operator (fluent)
   */


  equals(fieldName, value) {
    this.stringValue = this.isTemplateValue(value.toString()) ? `${fieldName}:${value.toString()}` : `${fieldName}:'${value.toString()}'`;
    this.segmentType = 'equals';
    return this.finialize();
  }
  /**
   * Field equality and NOT operator combination. (e.g. +NOT(FieldName:'value'))
   * @param { K } FieldName The name of the Field to be checked
   * @param { TReturns[K] } value The value that will be checked. You can use '?' and '*' wildcards
   * @returns { QueryOperator<TReturns> } The Next query operator (fluent)
   */


  notEquals(fieldName, value) {
    this.stringValue = this.isTemplateValue(value.toString()) ? `NOT(${fieldName}:${value.toString()})` : `NOT(${fieldName}:'${value.toString()}')`;
    this.segmentType = 'notEquals';
    return this.finialize();
  }
  /**
   * Field equality check on nested  field query expression (e.g. +FieldName:{{NestedFieldName:'value'}})
   * @param { K } fieldName The name of the Field to be checked
   * @param { K } nestedFieldName The name of the nested Field to be checked
   * @param { TReturns[K] } value The value that will be checked. You can use '?' and '*' wildcards
   * @returns { QueryOperator<TReturns> } The Next query operator (fluent)
   */


  equalsNested(fieldName, nestedFieldName, value) {
    this.stringValue = `${fieldName}:{{${nestedFieldName}:${value.toString()}}}`;
    this.segmentType = 'equalsNested';
    return this.finialize();
  }
  /**
   * Field equality check on nested field and NOT operator combination (e.g. +NOT(FieldName:{{NestedFieldName:'value'}}))
   * @param { K } fieldName The name of the Field to be checked
   * @param { K } nestedFieldName The name of the nested Field to be checked
   * @param { TReturns[K] } value The value that will be checked. You can use '?' and '*' wildcards
   * @returns { QueryOperator<TReturns> } The Next query operator (fluent)
   */


  notEqualsNested(fieldName, nestedFieldName, value) {
    this.stringValue = `NOT(${fieldName}:{{${nestedFieldName}:${value.toString()}}})`;
    this.segmentType = 'notEqualsNested';
    return this.finialize();
  }
  /**
   * Range search query expression
   * @param { K } fieldName he name of the Field to be checked
   * @param { TReturns[K] } minValue The minimum allowed value
   * @param { TReturns[K] } maxValue The maximum allowed value
   * @param { boolean } minimumInclusive Lower limit will be inclusive / exclusive
   * @param { boolean } maximumInclusive Upper limit will be inclusive / exclusive
   */


  between(fieldName, minValue, maxValue) {
    let minimumInclusive = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    let maximumInclusive = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
    this.stringValue = `${fieldName}:${minimumInclusive ? '[' : '{'}'${minValue.toString()}' TO '${maxValue.toString()}'${maximumInclusive ? ']' : '}'}`;
    this.segmentType = 'between';
    return this.finialize();
  }
  /**
   * Greater than query expression (+FieldName:>'value')
   * @param { K } fieldName he name of the Field to be checked
   * @param { TReturns[K] } minValue The minimum allowed value
   * @param { boolean } minimumInclusive Lower limit will be inclusive / exclusive
   */


  greaterThan(fieldName, minValue) {
    let minimumInclusive = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    this.stringValue = this.isTemplateValue(minValue.toString()) ? `${fieldName}:>${minimumInclusive ? '=' : ''}${minValue.toString()}` : `${fieldName}:>${minimumInclusive ? '=' : ''}'${minValue.toString()}'`;
    this.segmentType = 'greaterThan';
    return this.finialize();
  }
  /**
   * Less than query expression (+FieldName:<'value')
   * @param { K } fieldName he name of the Field to be checked
   * @param { TReturns[K] } maxValue The maximum allowed value
   * @param { boolean } maximumInclusive Upper limit will be inclusive / exclusive
   */


  lessThan(fieldName, maxValue) {
    let maximumInclusive = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    this.stringValue = this.isTemplateValue(maxValue.toString()) ? `${fieldName}:<${maximumInclusive ? '=' : ''}${maxValue.toString()}` : `${fieldName}:<${maximumInclusive ? '=' : ''}'${maxValue.toString()}'`;
    this.segmentType = 'lessThan';
    return this.finialize();
  }
  /**
   * A Nested query expression
   * @param {(first: QueryExpression<TReturns>) => QuerySegment<TReturns>)} build The Expression builder method
   */


  query(build) {
    this.subQuery = build instanceof Query ? build : new Query(build);
    this.stringValue = `(${this.subQuery.toString()})`;
    this.segmentType = 'query';
    return this.finialize();
  }
  /**
   * A Nested NOT query expression
   * @param {(first: QueryExpression<TReturns>) => QuerySegment<TReturns>)} build The Expression builder method
   */


  not(build) {
    this.subQuery = build instanceof Query ? build : new Query(build);
    this.stringValue = `NOT(${this.subQuery.toString()})`;
    this.segmentType = 'not';
    return this.finialize();
  }
  /**
   * Finializes the Query expression
   */


  finialize() {
    this.queryRef.addSegment(this);
    return new QueryOperators(this.queryRef);
  }

}
//# sourceMappingURL=QueryExpression.js.map